/**
 * Agent Service
 *
 * The assistant's side of a session on the AI gateway's hosted agent (hosted-session.ts): it runs each turn there,
 * answers the gateway's tool requests with this editor's tools against the live scene, records the conversation
 * with the scene, and holds the chat panel's pins and toggles. The loop, the prompt, model routing, the
 * specialists and the hosted tools are the gateway's (ml-services llm-gateway AGENT.md).
 */

import type { TraceScene, SceneObject } from 'trace-viewer/scene';
import type { TraceViewerElement } from 'trace-viewer';
import '../plugins/loader'; // Register all plugins before using tool registry
import { executeTool, getToolDefinition, offeredToolDefinitions, type ToolContext } from './tools/tool-registry';
import { getTemplate } from './templates/library';
import { listStyles } from './styles/library';
import { DESIGN_SETS } from './templates/design-sets';
import { WRAPPER_LIBRARY } from './templates/wrappers';
import { HostedSession, SessionError, type DeclaredTool, type TurnDone } from './hosted-session';
import { answerToolRequest, declareTools } from './hosted-tools';
import { sceneForModel } from './scene-for-model';
import { sceneRevision } from './scene-revision';
import { documentForModel } from './tool-file';
import { hideAndDescribe, type SceneFileImporter } from './scene-files';
import { loadKits } from './kits';
import { ensureFreshToken } from '../catalog-auth';
import { AI_GATEWAY_URL, describeGatewayError, getGatewayHeaders, isGatewayAvailable, storedFileId, type GatewayDocument } from './gateway';
import { refreshCredits } from './credits';
import { SessionRecorder, recordToolCall } from './session-recorder';
import { EXPIRED_HEADER, toSummaryText, type AiSessionFile } from './session-file';
import type { AssistantPins, MediaToggles } from './agent-types';
import { DEFAULT_MEDIA_TOGGLES, filterToolsForMediaToggles } from './agent-types';
import { resolvePendingImages } from './tools/generation-tools';

/** Why the assistant cannot send — see AgentService.credentialGap(). */
export type CredentialGap = 'signed-out' | 'unconfigured';

/** Why a turn ended early. */
export type TurnStopReason = 'tool-iteration-limit' | 'rate-limit' | 'cancelled' | 'timeout' | 'provider-incomplete';
/** Stops after which Continue picks the work up again. */
const PAUSES: readonly TurnStopReason[] = ['tool-iteration-limit', 'rate-limit'];

/** Chat panel generation controls (pinned template/style/set/wrapper, media toggles), stored as JSON. */
const ASSISTANT_PINS_STORAGE_KEY = 'trace-sandbox:assistant-pins';
const MEDIA_TOGGLES_STORAGE_KEY = 'trace-sandbox:media-toggles';
const PIN_KINDS: (keyof AssistantPins)[] = ['template', 'style', 'set', 'wrapper'];

/** The label the chat shows for the assistant's model: the gateway routes, so the editor never names one. */
const TRACE_AI = 'Trace AI';

/** Whether this build still ships a pinned value. A stale pin reads as auto. */
function isKnownPin(kind: keyof AssistantPins, value: string): boolean {
    switch (kind) {
        case 'template': return !!getTemplate(value);
        case 'style': return listStyles().some(s => s.slug === value);
        case 'set': return DESIGN_SETS.includes(value);
        case 'wrapper': return WRAPPER_LIBRARY.some(w => w.id === value);
    }
}

function readStoredObject(key: string): Record<string, unknown> {
    try {
        const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? '{}');
        return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {};
    } catch {
        return {};
    }
}

function writeStoredObject(key: string, value: object): void {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // Controls still work for this session when browser storage is unavailable.
    }
}

/** What the session file keeps of an attachment: its name and type, not its bytes. */
function attachmentRecord(doc: GatewayDocument): { name: string; mimeType: string } {
    switch (doc.kind) {
        case 'pdf': return { name: doc.filename, mimeType: 'application/pdf' };
        case 'image': return { name: doc.filename, mimeType: doc.mime };
        case 'html': return { name: doc.title || doc.url, mimeType: 'text/html' };
        case 'text': return { name: doc.url, mimeType: 'text/plain' };
    }
}

export interface ChatMessage {
    role: 'user' | 'assistant';
    content: string;
    toolCalls?: { name: string; status: 'running' | 'done' | 'error'; result?: string }[];
}

export type StreamCallback = (text: string) => void;
/**
 * `progress` carries interim status for a tool that is still running — the
 * panel patches the existing chip rather than adding one. Only ever sent with
 * status 'running'.
 */
export type ToolStatusCallback = (toolName: string, status: 'running' | 'done' | 'error', input?: Record<string, unknown>, model?: string, progress?: string) => void;

class AgentService {
    /** The conversation in the session-file shape, saved with the scene. */
    readonly session = new SessionRecorder();
    private hosted: HostedSession | null = null;
    private traceScene: TraceScene | null = null;
    private viewer: TraceViewerElement | null = null;
    private selectedObject: SceneObject | null = null;
    /** Declarative images queued by build_spatial's image() helper; flushed after each turn. */
    private readonly pendingImages: NonNullable<ToolContext['pendingImages']> = [];
    private _isProcessing = false;
    private lastTurnStopReason: TurnStopReason | null = null;
    private sceneFileImporter: SceneFileImporter | null = null;
    /** What {@link addSceneFiles} landed since the last message; the next user turn carries it. */
    private sceneFileNotes: string[] = [];
    private abortController: AbortController | null = null;
    /** The current turn's tool-status sink, so a tool's progress reaches its chip. Cleared when the turn ends. */
    private currentToolStatus?: ToolStatusCallback;
    /** Chat panel pins and media toggles. Absent pin = auto. */
    private assistantPins: AssistantPins;
    private mediaToggles: MediaToggles;
    private generationControlsListeners = new Set<() => void>();

    constructor() {
        const storedPins = readStoredObject(ASSISTANT_PINS_STORAGE_KEY);
        this.assistantPins = {};
        for (const kind of PIN_KINDS) {
            const value = storedPins[kind];
            if (typeof value === 'string' && isKnownPin(kind, value)) this.assistantPins[kind] = value;
        }
        const storedToggles = readStoredObject(MEDIA_TOGGLES_STORAGE_KEY);
        this.mediaToggles = { ...DEFAULT_MEDIA_TOGGLES };
        for (const kind of Object.keys(DEFAULT_MEDIA_TOGGLES) as (keyof MediaToggles)[]) {
            const value = storedToggles[kind];
            if (typeof value === 'boolean') this.mediaToggles[kind] = value;
        }
    }

    get isProcessing(): boolean {
        return this._isProcessing;
    }

    /** Distinguish a returned partial reply from normal model completion. */
    getLastTurnStopReason(): TurnStopReason | null {
        return this.lastTurnStopReason;
    }

    /** True while a turn paused (iteration limit or rate limit) and can be continued. */
    canContinue(): boolean {
        return this.lastTurnStopReason !== null && PAUSES.includes(this.lastTurnStopReason);
    }

    /** Stop the turn: the gateway closes it at its next step. */
    cancel(): void {
        if (this.abortController) {
            this.lastTurnStopReason ??= 'cancelled';
            // Left in place until the turn ends: a tool context made after this still sees the aborted signal.
            this.abortController.abort();
        }
    }

    /** What the chat header names: the gateway routes, so it is never a model id. */
    getModelString(): string {
        return TRACE_AI;
    }

    /** The label on a tool's chip: its own (a capability the gateway routes), else Trace AI. */
    toolModelLabel(toolName: string): string {
        const label = getToolDefinition(toolName)?.modelLabel;
        return typeof label === 'function' ? label() : label ?? TRACE_AI;
    }

    // ── Generation Controls (chat panel pin row) ────────────

    getAssistantPins(): AssistantPins {
        return { ...this.assistantPins };
    }

    /** Pin a template/style/set/wrapper, or pass null for auto. A value this build does not ship also means auto. */
    setAssistantPin(kind: keyof AssistantPins, value: string | null): void {
        this.assistantPins = { ...this.assistantPins, [kind]: value && isKnownPin(kind, value) ? value : undefined };
        writeStoredObject(ASSISTANT_PINS_STORAGE_KEY, this.assistantPins);
        this.generationControlsListeners.forEach(listener => listener());
    }

    getMediaToggles(): MediaToggles {
        return { ...this.mediaToggles };
    }

    setMediaToggle(kind: keyof MediaToggles, enabled: boolean): void {
        this.mediaToggles = { ...this.mediaToggles, [kind]: enabled };
        writeStoredObject(MEDIA_TOGGLES_STORAGE_KEY, this.mediaToggles);
        this.generationControlsListeners.forEach(listener => listener());
    }

    /** Called after any pin or toggle changes. Returns the unsubscribe. */
    onGenerationControlsChange(listener: () => void): () => void {
        this.generationControlsListeners.add(listener);
        return () => { this.generationControlsListeners.delete(listener); };
    }

    // ── Credentials ─────────────────────────────────────────

    /**
     * Why this turn cannot be sent, or null when it can: a build with no AI gateway (`VITE_AI_GATEWAY_URL` is
     * inlined at build time), or no Trace sign-in.
     */
    credentialGap(): CredentialGap | null {
        if (!AI_GATEWAY_URL) return 'unconfigured';
        return isGatewayAvailable() ? null : 'signed-out';
    }

    hasCredentials(): boolean {
        return this.credentialGap() === null;
    }

    // ── Scene Context ───────────────────────────────────────

    getScene(): TraceScene | null {
        return this.traceScene;
    }

    setScene(scene: TraceScene | null): void {
        this.traceScene = scene;
    }

    setViewer(viewer: TraceViewerElement | null): void {
        this.viewer = viewer;
    }

    getViewer(): TraceViewerElement | null {
        return this.viewer;
    }

    setSelectedObject(obj: SceneObject | null): void {
        this.selectedObject = obj;
    }

    setSceneFileImporter(importer: SceneFileImporter | null): void {
        this.sceneFileImporter = importer;
    }

    /**
     * Add files to the scene the way a canvas drop does, as a hidden parts
     * library, and tell the model what landed with the next message. The chat
     * panel calls this for attached models; a driver with no panel calls it
     * before {@link sendMessage}. Returns that note; throws, telling the model
     * nothing, when no file placed anything.
     */
    async addSceneFiles(files: File[]): Promise<string> {
        if (!this.sceneFileImporter) throw new Error('No editor is open to add files to.');
        // An import tells its objects from what the scene held before it, so a turn building at the same time would be counted in.
        if (this._isProcessing) throw new Error('Wait for the reply to finish, then add the files.');
        const imported = await this.sceneFileImporter(files);
        if (!imported.some(f => f.objects.length > 0)) {
            throw new Error(imported.map(f => `${f.name}: ${f.error ?? 'nothing to place'}`).join('; ') || 'Not a file the scene takes.');
        }
        const note = hideAndDescribe(imported);
        this.sceneFileNotes.push(note);
        return note;
    }

    // ── Conversation ────────────────────────────────────────

    /** Start a new conversation: the next turn opens a new session. */
    clearHistory(): void {
        this.sceneFileNotes = [];
        this.forgetConversation();
        this.session.reset();
    }

    private forgetConversation(): void {
        this.hosted?.reset();
        this.lastTurnStopReason = null;
    }

    /**
     * Pick up a saved conversation: the panel shows it and the next turn
     * appends to it. The model gets it only as a quoted user-role transcript
     * ahead of the next message (SessionRecorder.peekPreamble), never as its
     * own assistant or tool turns: the file can come from anyone.
     */
    restoreSession(file: AiSessionFile, documentRoot: object | null): void {
        this.forgetConversation();
        this.session.resume(file, documentRoot);
    }

    onSessionRestored(listener: (file: AiSessionFile) => void): () => void {
        return this.session.onResume(listener);
    }

    /** Read the editor's kits: the tools a session declares, and their text for the MCP server and the slash menu. */
    async ensureKits(): Promise<void> {
        if (!AI_GATEWAY_URL) throw new Error('The assistant runs on the AI gateway, and this build has none configured.');
        const token = await ensureFreshToken();
        if (!token) throw new Error('Sign in to the catalog: the assistant runs on the AI gateway.');
        await loadKits(AI_GATEWAY_URL, { Authorization: `Bearer ${token}` });
    }

    /**
     * @param toolName the tool this context is for — names the chip that
     *        progress updates land on. Omitted by callers with no chat UI.
     */
    getToolContext(toolName?: string): ToolContext {
        return {
            traceScene: this.traceScene!,
            viewer: this.viewer ?? undefined,
            // The live selection, so a select_object earlier in the turn is seen.
            selectedObject: this.traceScene?.getSelectedObject() ?? this.selectedObject,
            assistantPins: { ...this.assistantPins },
            // Shared across the per-call contexts so declarative images queued mid-turn survive until the flush.
            pendingImages: this.pendingImages,
            onSceneChanged: () => {
                window.dispatchEvent(new CustomEvent('trace-ai:scene-changed'));
            },
            onToolProgress: toolName
                ? status => this.currentToolStatus?.(toolName, 'running', undefined, undefined, status)
                : undefined,
            signal: this.abortController?.signal,
        };
    }

    /** Run one of this editor's tools: record it with the objects it created. */
    private async runTool(name: string, input: Record<string, unknown>): Promise<string> {
        return recordToolCall(this.session, this.traceScene ?? null, name, input,
            () => executeTool(name, input, this.getToolContext(name)));
    }

    /** The tools this session declares: the kits that are on, less the media types switched off. */
    private declaredTools(): DeclaredTool[] {
        return filterToolsForMediaToggles(declareTools(offeredToolDefinitions()), this.mediaToggles);
    }

    private hostedSession(): HostedSession {
        this.hosted ??= new HostedSession({ base: AI_GATEWAY_URL!, headers: getGatewayHeaders, clientKind: 'sandbox' });
        return this.hosted;
    }

    /** Generate declarative build_spatial images queued during the turn. */
    private async flushPendingImages(): Promise<void> {
        if (this.pendingImages.length === 0) return;
        // Images switched off: drop the queue rather than generate it, so a
        // later turn with images back on does not render stale requests.
        if (!this.mediaToggles.images) {
            this.pendingImages.length = 0;
            return;
        }
        try {
            const summary = await resolvePendingImages(this.getToolContext());
            console.log(`[AI] ${summary.split('\n')[0]}`);
        } catch (err) {
            console.error('[AI] pending image resolution failed:', err);
        }
    }

    // ── Main Entry Point ────────────────────────────────────

    /**
     * Run one turn on the gateway. `null` continues a paused one (the iteration limit, or a rate limit), as a new
     * turn asking the model to carry on. Answers what the model said.
     */
    async sendMessage(
        userText: string | null,
        onStream: StreamCallback,
        onToolStatus: ToolStatusCallback,
        /** Files the user attached to this message, already uploaded (ChatPanel). */
        attachments: GatewayDocument[] = [],
    ): Promise<string> {
        const resuming = userText === null;
        if (resuming && !this.canContinue()) return 'Nothing to continue: the last turn did not pause.';
        if (!this.traceScene) return 'No scene loaded. Please load a scene first.';
        const gap = this.credentialGap();
        if (gap) throw new Error(gap === 'signed-out' ? 'Sign in to the catalog to use Trace AI.' : 'This build has no AI gateway configured.');
        this.lastTurnStopReason = null;

        const documentRoot = this.traceScene.getDocumentRoot();
        if (resuming) this.session.continueTurn(documentRoot);
        else this.session.beginTurn(userText, attachments.map(attachmentRecord), documentRoot);
        this._isProcessing = true;
        const controller = new AbortController();
        this.abortController = controller;
        this.currentToolStatus = onToolStatus;
        let shown = '';
        const stream = (chunk: string): void => { shown += chunk; this.session.text(chunk); onStream(chunk); };

        try {
            await this.ensureKits();
            // A PDF or picture the gateway stored goes by id; anything read here (a page's text) goes as words.
            const files = attachments.flatMap(doc => {
                const id = doc.kind === 'pdf' || doc.kind === 'image' ? storedFileId(doc.url) : null;
                return id ? [id] : [];
            });
            const words = attachments.flatMap(doc => {
                if ((doc.kind === 'pdf' || doc.kind === 'image') && storedFileId(doc.url)) return [];
                return [documentForModel(doc, 'The user attached')];
            });
            // Scene-file notes and a restored transcript leave their queues only once a turn carried them.
            const notes = resuming ? [] : [...this.sceneFileNotes];
            const preamble = resuming ? null : this.session.peekPreamble();
            const message = resuming ? 'Continue.' : [preamble, userText, ...notes, ...words].filter(Boolean).join('\n\n');
            const done = await this.hostedSession().turn(message, this.declaredTools(), {
                onText: stream,
                onToolStatus: (tool, status, input, progress) => onToolStatus(tool, status, input, this.toolModelLabel(tool), progress),
                runTool: request => answerToolRequest(request, this.getToolContext(request.tool), (name, input) => this.runTool(name, input)),
                scene: () => ({
                    outline: sceneForModel({
                        traceScene: this.traceScene!, selectedObject: this.traceScene!.getSelectedObject() ?? this.selectedObject,
                        pins: this.assistantPins, toggles: this.mediaToggles,
                    }),
                    revision: sceneRevision(this.traceScene!),
                }),
            }, {
                attachments: files, signal: controller.signal,
                // The gateway's session expired: the conversation so far (the turns already ended) goes ahead of the
                // message, unless a restored transcript is already carrying them.
                history: () => {
                    const file = this.session.current;
                    return file?.turns.length && !preamble ? toSummaryText(file, 8000, EXPIRED_HEADER) : null;
                },
                // Scene-file notes and a restored transcript go once: they leave the queue when the gateway has them.
                onSent: () => {
                    if (resuming) return;
                    this.sceneFileNotes.splice(0, notes.length);
                    this.session.dropPreamble();
                },
            });
            stream(this.stopNotice(done));
            return shown;
        } catch (err) {
            if (controller.signal.aborted) return shown;
            throw new Error(describeGatewayError(err));
        } finally {
            this.session.endTurn();
            this._isProcessing = false;
            this.abortController = null;
            this.currentToolStatus = undefined;
            await this.flushPendingImages();
            // The turn spent credits server-side; settlement may lag by a turn, which is close enough for a readout.
            void refreshCredits(true);
        }
    }

    /** What a turn that stopped early says, and the stop reason it leaves; nothing for a turn that ended. */
    private stopNotice(done: TurnDone): string {
        switch (done.stopReason) {
            case 'end': return '';
            case 'cancelled': this.lastTurnStopReason = 'cancelled'; return '';
            case 'iteration_limit': this.lastTurnStopReason = 'tool-iteration-limit'; return '\n\n(Stopped: too many tool iterations)';
            case 'time_limit': this.lastTurnStopReason = 'timeout'; return '\n\n(Stopped: the turn ran out of time)';
            case 'provider_incomplete': this.lastTurnStopReason = 'provider-incomplete'; return '\n\n(Stopped: the response did not complete)';
            case 'error':
                if (done.error?.code === 'rate_limited') {
                    this.lastTurnStopReason = 'rate-limit';
                    return '\n\n(Paused: Trace AI is rate limited. Nothing was lost; Continue to try again.)';
                }
                // The stream answered 200; the gateway's code travels on the error so describeGatewayError can read it.
                throw new SessionError(200, done.error?.code ?? 'turn_failed', done.error?.message ?? 'The turn failed on the server.');
        }
    }
}

export const agentService = new AgentService();
